import { useRef, useState } from 'react';
import { toast } from 'sonner';

/**
 * useDrawing — encapsulates all canvas pointer logic for the PlayEditor.
 *
 * Owns: dragId, arrowStart, strokePoints, assignTargetId (double-tap modal target),
 * lastTapRef, svgPoint(), and the player/canvas pointer handlers.
 *
 * The hook calls back into the host with player CRUD when needed:
 *   - addPlayer(team, pos) when toolbar is in addOff/addDef mode
 *   - removePlayer(id)     when toolbar is in eraser mode
 *   - setSteps(updater)    for any other state mutation
 *
 * The 8-unit attach threshold matches the original behavior: a freehand
 * stroke that starts within ~8% of a player token snaps to that player and
 * becomes their motion path; otherwise it is kept as a decorative annotation.
 */
const ATTACH_THRESHOLD = 8;

export default function useDrawing({
  steps,
  setSteps,
  activeIdx,
  tool,
  svgRef,
  addPlayer,
  removePlayer,
}) {
  const [dragId, setDragId] = useState(null);
  const [arrowStart, setArrowStart] = useState(null);
  const [strokePoints, setStrokePoints] = useState(null);
  const [assignTargetId, setAssignTargetId] = useState(null);
  const lastTapRef = useRef({ id: null, t: 0 });

  const active = steps[activeIdx];

  const svgPoint = (e) => {
    const svg = svgRef.current;
    if (!svg) return { x: 50, y: 50 };
    const pt = svg.createSVGPoint();
    const cx = e.clientX ?? (e.touches?.[0]?.clientX) ?? 0;
    const cy = e.clientY ?? (e.touches?.[0]?.clientY) ?? 0;
    pt.x = cx; pt.y = cy;
    const m = svg.getScreenCTM();
    if (!m) return { x: 50, y: 50 };
    const c = pt.matrixTransform(m.inverse());
    return {
      x: Math.max(0, Math.min(100, (c.x / 1000) * 100)),
      y: Math.max(0, Math.min(100, (c.y / 1000) * 100)),
    };
  };

  const onPlayerPointerDown = (pid) => (e) => {
    e.stopPropagation();
    const now = Date.now();
    if (lastTapRef.current.id === pid && now - lastTapRef.current.t < 400) {
      setAssignTargetId(pid);
      lastTapRef.current = { id: null, t: 0 };
      setDragId(null);
      setArrowStart(null);
      return;
    }
    lastTapRef.current = { id: pid, t: now };

    if (tool === 'select') {
      setDragId(pid);
      svgRef.current?.setPointerCapture?.(e.pointerId);
    } else if (tool === 'eraser') {
      removePlayer(pid);
    } else if (['move', 'pass', 'screen', 'dribble'].includes(tool)) {
      const p = active.players.find(pl => pl.id === pid);
      if (p) setArrowStart({ from: { x: p.position.x, y: p.position.y }, pid });
    }
  };

  const onCanvasPointerDown = (e) => {
    if (['circle', 'image', 'text'].includes(e.target.tagName)) return;
    const pt = svgPoint(e);
    if (tool === 'freehand') {
      setStrokePoints([pt]);
      svgRef.current?.setPointerCapture?.(e.pointerId);
    } else if (tool === 'addOff') {
      addPlayer('offense', pt);
    } else if (tool === 'addDef') {
      addPlayer('defense', pt);
    }
  };

  const onCanvasPointerMove = (e) => {
    if (strokePoints) {
      const pt = svgPoint(e);
      setStrokePoints(sp => [...(sp || []), pt]);
      return;
    }
    if (dragId) {
      const { x, y } = svgPoint(e);
      setSteps(prev => prev.map((st, i) => i !== activeIdx ? st : ({
        ...st,
        players: st.players.map(pl => pl.id === dragId ? { ...pl, position: { x, y } } : pl),
      })));
    }
  };

  const onCanvasPointerUp = (e) => {
    if (strokePoints && strokePoints.length > 1) {
      const pts = strokePoints;
      setStrokePoints(null);

      const start = pts[0];
      const end = pts[pts.length - 1];
      let bestId = null;
      let bestDist = Infinity;
      for (const p of active.players) {
        const d = Math.hypot(p.position.x - start.x, p.position.y - start.y);
        if (d < bestDist) { bestDist = d; bestId = p.id; }
      }

      if (bestId && bestDist <= ATTACH_THRESHOLD) {
        setSteps(prev => {
          const next = [...prev];
          next[activeIdx] = {
            ...next[activeIdx],
            paths: { ...(next[activeIdx].paths || {}), [bestId]: { points: pts, speed: 1.0, delay: 0 } },
          };
          const nextIdx = activeIdx + 1;
          if (nextIdx >= next.length) {
            const newStepNumber = next.length + 1;
            const newStep = {
              step_number: newStepNumber,
              description: `Paso ${newStepNumber}`,
              players: next[activeIdx].players.map(pl =>
                pl.id === bestId ? { ...pl, position: { x: end.x, y: end.y } } : { ...pl }
              ),
              arrows: [],
              strokes: [],
              paths: {},
            };
            next.push(newStep);
          } else {
            next[nextIdx] = {
              ...next[nextIdx],
              players: next[nextIdx].players.map(pl =>
                pl.id === bestId ? { ...pl, position: { x: end.x, y: end.y } } : pl
              ),
            };
          }
          return next;
        });
        const target = active.players.find(p => p.id === bestId);
        const label = target?.player_name || `#${target?.label || ''}`;
        toast.success(`Ruta asignada a ${label}`);
      } else {
        setSteps(prev => prev.map((st, i) => i !== activeIdx ? st : ({
          ...st,
          strokes: [...(st.strokes || []), { points: pts, color: '#D4AF37', width: 6, id: `s${Date.now()}` }],
        })));
      }
      return;
    }
    if (dragId) { setDragId(null); return; }
    if (arrowStart) {
      const { x, y } = svgPoint(e);
      setSteps(prev => prev.map((st, i) => i !== activeIdx ? st : ({
        ...st,
        arrows: [...(st.arrows || []), { from: arrowStart.from, to: { x, y }, type: tool }],
      })));
      setArrowStart(null);
    }
  };

  return {
    // state
    dragId,
    arrowStart,
    strokePoints,
    assignTargetId,
    setAssignTargetId,
    // helpers
    svgPoint,
    cancel: () => { setDragId(null); setStrokePoints(null); setArrowStart(null); },
    // handlers
    onPlayerPointerDown,
    onCanvasPointerDown,
    onCanvasPointerMove,
    onCanvasPointerUp,
  };
}
